import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './RapportCaisse.css';

interface StatsJour {
  nombreVentes: number;
  chiffreAffaires: number;
  parMode: {
    especes: number;
    mobileMoney: number;
    credit: number;
  };
}

interface Vente {
  id: number;
  numero: string;
  clientNom: string;
  totalTTC: number;
  modePaiement: string;
  statut: string;
  createdAt: string;
}

interface DepensesJour {
  totalJour: number;
  especes: { total: number; lignes: { libelle: string; categorie: string; montant: number }[] };
  autresModes: number;
}

const fmt = (n: number) => Math.round(Number(n)).toLocaleString('fr-FR');

const modeLabel = (mode: string) => {
  const labels: any = {
    especes: '💵 Espèces',
    orange_money: '🟠 Orange Money',
    moov_money: '🟡 Moov Money',
    wave: '🔵 Wave',
    credit: '💳 Crédit',
    virement: '🏦 Virement',
  };
  return labels[mode] || mode;
};

export default function RapportCaisse() {
  const [stats, setStats] = useState<StatsJour | null>(null);
  const [ventes, setVentes] = useState<Vente[]>([]);
  const [depensesJour, setDepensesJour] = useState<DepensesJour | null>(null);
  const [fondCaisse, setFondCaisse] = useState(0);
  const [chargement, setChargement] = useState(true);
  const [dateRapport] = useState(new Date());
  const logo = localStorage.getItem('nexacom_logo');

  useEffect(() => {
    chargerDonnees();
  }, []);

  const chargerDonnees = async () => {
    try {
      const [statsRes, ventesRes, depRes, boutiquesRes] = await Promise.all([
        api.get('/ventes/stats/jour'),
        api.get('/ventes'),
        api.get('/depenses/stats/jour').catch(() => ({ data: null })),
        api.get('/boutiques').catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setDepensesJour(depRes.data);
      // Fond de caisse = somme des fonds paramétrés des boutiques du tenant.
      setFondCaisse(
        (boutiquesRes.data as any[]).reduce((s, b) => s + Number(b.fondCaisse || 0), 0),
      );
      // Filtrer uniquement les ventes du jour
      const aujourdhui = new Date().toLocaleDateString('fr-FR');
      setVentes(ventesRes.data.filter((v: Vente) =>
        new Date(v.createdAt).toLocaleDateString('fr-FR') === aujourdhui
      ));
    } catch (e) {
    } finally {
      setChargement(false);
    }
  };

  const imprimer = () => window.print();

  if (chargement) return <div className="page-chargement">Chargement du rapport...</div>;

  return (
    <div className="rapport-page">
      <div className="rapport-actions no-print">
        <div>
          <h1>Rapport de caisse journalier</h1>
          <p>Clôture du {dateRapport.toLocaleDateString('fr-FR', {
            weekday: 'long', year: 'numeric',
            month: 'long', day: 'numeric'
          })}</p>
        </div>
        <button className="btn-imprimer" onClick={imprimer}>
          🖨️ Imprimer le rapport
        </button>
      </div>

      {/* RAPPORT IMPRIMABLE */}
      <div className="rapport-document zone-impression">
        {/* EN-TÊTE */}
        <div className="rapport-entete">
          {logo ? (
            <img src={logo} alt="Logo" className="rapport-logo-img" />
          ) : (
            <div className="rapport-logo">NexaCom</div>
          )}
          <div className="rapport-titre-doc">
            <div className="rapport-titre">RAPPORT DE CAISSE JOURNALIER</div>
            <div className="rapport-date">
              Date : {dateRapport.toLocaleDateString('fr-FR')} —
              Heure de clôture : {dateRapport.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        </div>

        {/* RÉSUMÉ FINANCIER */}
        <div className="rapport-section">
          <div className="rapport-section-titre">RÉSUMÉ FINANCIER</div>
          <div className="rapport-resume-grid">
            <div className="rr-card principal">
              <div className="rr-label">CHIFFRE D'AFFAIRES DU JOUR</div>
              <div className="rr-val">{fmt(stats?.chiffreAffaires || 0)} FCFA</div>
              <div className="rr-sub">{stats?.nombreVentes || 0} transaction{(stats?.nombreVentes || 0) > 1 ? 's' : ''}</div>
            </div>
            <div className="rr-card">
              <div className="rr-label">💵 Encaissements Espèces</div>
              <div className="rr-val especes">{fmt(stats?.parMode?.especes || 0)} FCFA</div>
            </div>
            <div className="rr-card">
              <div className="rr-label">📱 Mobile Money</div>
              <div className="rr-val mobile">{fmt(stats?.parMode?.mobileMoney || 0)} FCFA</div>
            </div>
            <div className="rr-card">
              <div className="rr-label">💳 Ventes à Crédit</div>
              <div className="rr-val credit">{fmt(stats?.parMode?.credit || 0)} FCFA</div>
            </div>
          </div>
        </div>

        {/* DÉTAIL PAR MODE DE PAIEMENT */}
        <div className="rapport-section">
          <div className="rapport-section-titre">DÉTAIL PAR MODE DE PAIEMENT</div>
          <table className="rapport-table">
            <thead>
              <tr>
                <th>Mode de paiement</th>
                <th>Nb. transactions</th>
                <th>Montant total</th>
                <th>% du CA</th>
              </tr>
            </thead>
            <tbody>
              {[
                { mode: 'especes', label: '💵 Espèces', montant: stats?.parMode?.especes || 0 },
                { mode: 'mobile', label: '📱 Mobile Money', montant: stats?.parMode?.mobileMoney || 0 },
                { mode: 'credit', label: '💳 Crédit', montant: stats?.parMode?.credit || 0 },
              ].map(row => {
                const nb = ventes.filter(v =>
                  row.mode === 'mobile'
                    ? ['orange_money', 'moov_money', 'wave'].includes(v.modePaiement)
                    : v.modePaiement === row.mode
                ).length;
                const pct = stats?.chiffreAffaires
                  ? ((row.montant / stats.chiffreAffaires) * 100).toFixed(1)
                  : '0.0';
                return (
                  <tr key={row.mode}>
                    <td>{row.label}</td>
                    <td className="text-center">{nb}</td>
                    <td className="text-right">{fmt(row.montant)} FCFA</td>
                    <td className="text-center">{pct}%</td>
                  </tr>
                );
              })}
              <tr className="total-row">
                <td><strong>TOTAL</strong></td>
                <td className="text-center"><strong>{stats?.nombreVentes || 0}</strong></td>
                <td className="text-right"><strong>{fmt(stats?.chiffreAffaires || 0)} FCFA</strong></td>
                <td className="text-center"><strong>100%</strong></td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* RAPPROCHEMENT DE CAISSE (ESPÈCES) */}
        {(() => {
          const encEspeces = stats?.parMode?.especes || 0;
          const sorties = depensesJour?.especes?.total || 0;
          const attendues = fondCaisse + encEspeces - sorties;
          return (
            <div className="rapport-section">
              <div className="rapport-section-titre">RAPPROCHEMENT DE CAISSE (ESPÈCES)</div>
              <table className="rapport-table rappro-table">
                <tbody>
                  <tr>
                    <td>Fond de caisse (paramétré)</td>
                    <td className="text-right">+ {fmt(fondCaisse)} FCFA</td>
                  </tr>
                  <tr>
                    <td>Encaissements en espèces</td>
                    <td className="text-right">+ {fmt(encEspeces)} FCFA</td>
                  </tr>
                  <tr>
                    <td>Sorties en espèces (dépenses validées)</td>
                    <td className="text-right">− {fmt(sorties)} FCFA</td>
                  </tr>
                  <tr className="total-row">
                    <td><strong>ESPÈCES ATTENDUES EN CAISSE</strong></td>
                    <td className="text-right"><strong>{fmt(attendues)} FCFA</strong></td>
                  </tr>
                </tbody>
              </table>

              <div className="rappro-comptage">
                <div className="rc-row"><span>Espèces réellement comptées :</span><span className="rc-blank" /></div>
                <div className="rc-row"><span>Écart (comptées − attendues) :</span><span className="rc-blank" /></div>
              </div>

              {depensesJour && depensesJour.especes.lignes.length > 0 && (
                <table className="rapport-table" style={{ marginTop: 12 }}>
                  <thead>
                    <tr><th>Sortie en espèces</th><th>Catégorie</th><th>Montant</th></tr>
                  </thead>
                  <tbody>
                    {depensesJour.especes.lignes.map((l, i) => (
                      <tr key={i}>
                        <td>{l.libelle}</td>
                        <td>{l.categorie}</td>
                        <td className="text-right">{fmt(l.montant)} FCFA</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <div className="rappro-note">
                Seules les dépenses <strong>validées payées en espèces</strong> sont déduites.
                {depensesJour && depensesJour.autresModes > 0
                  ? ` Dépenses du jour réglées autrement (mobile money, virement…) : ${fmt(depensesJour.autresModes)} FCFA — sans effet sur la caisse.`
                  : ''}
              </div>
            </div>
          );
        })()}

        {/* LISTE DES TRANSACTIONS */}
        <div className="rapport-section">
          <div className="rapport-section-titre">LISTE DES TRANSACTIONS DU JOUR</div>
          {ventes.length === 0 ? (
            <div className="rapport-empty">Aucune transaction enregistrée aujourd'hui</div>
          ) : (
            <table className="rapport-table">
              <thead>
                <tr>
                  <th>N° Facture</th>
                  <th>Heure</th>
                  <th>Client</th>
                  <th>Mode paiement</th>
                  <th>Statut</th>
                  <th>Montant TTC</th>
                </tr>
              </thead>
              <tbody>
                {ventes.map(v => (
                  <tr key={v.id}>
                    <td className="fact-num">{v.numero}</td>
                    <td>{new Date(v.createdAt).toLocaleTimeString('fr-FR', {
                      hour: '2-digit', minute: '2-digit'
                    })}</td>
                    <td>{v.clientNom || 'Client de passage'}</td>
                    <td>{modeLabel(v.modePaiement)}</td>
                    <td>
                      <span className={`statut-mini ${v.statut}`}>
                        {v.statut === 'payee' ? '✓ Payée' :
                          v.statut === 'credit' ? 'Crédit' : v.statut}
                      </span>
                    </td>
                    <td className="text-right montant">{fmt(v.totalTTC)} FCFA</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td colSpan={5}><strong>TOTAL JOURNÉE</strong></td>
                  <td className="text-right">
                    <strong>{fmt(stats?.chiffreAffaires || 0)} FCFA</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        {/* SIGNATURES */}
        <div className="rapport-signatures">
          <div className="sig-bloc">
            <div className="sig-titre">Le Caissier / La Caissière</div>
            <div className="sig-ligne"></div>
            <div className="sig-nom">Nom & Signature</div>
          </div>
          <div className="sig-bloc">
            <div className="sig-titre">Le Gérant / La Gérante</div>
            <div className="sig-ligne"></div>
            <div className="sig-nom">Nom & Signature</div>
          </div>
          <div className="sig-bloc">
            <div className="sig-titre">Visa Direction</div>
            <div className="sig-ligne"></div>
            <div className="sig-nom">Cachet & Signature</div>
          </div>
        </div>

        <div className="rapport-pied">
          Document généré par NexaCom le {dateRapport.toLocaleString('fr-FR')} —
          Confidentiel, usage interne uniquement
        </div>
      </div>
    </div>
  );
}